/**
 * useModerationTimerGuard hook
 *
 * Encapsulates the session TTL countdown timer and navigation guard logic.
 * Extracted from useModerationWorkQueueSetup to keep statement count within
 * the max-statements (≤ 20) ESLint limit.
 */

import { useQuery } from '@apollo/client';
import { useCommunityTopics } from '@communities/content-management/hooks';
import type { GetTopicModerationStatsQuery } from '@core/graphql/types/operations';
import type { ModerationCase } from '@moderation/domain/ModerationCase';
import { GET_TOPIC_MODERATION_STATS } from '@moderation/graphql';
import { useMemo } from 'react';
import type { Blocker } from 'react-router-dom';

import { useModerationCountdown } from './useModerationCountdown';
import type { UseModerationCountdownReturn } from './useModerationCountdown';
import { useModerationNavigationGuard } from './useModerationNavigationGuard';

/** Parameters for the timer guard hook */
interface UseModerationTimerGuardParams {
  communityId: string;
  topicName: string;
  sessionCases: ModerationCase[];
  isActive: boolean;
}

/** Return value from the timer guard hook */
export interface ModerationTimerGuardResult {
  /** Session TTL countdown timer state */
  countdown: UseModerationCountdownReturn;
  /** React Router blocker for unsaved-work navigation guard */
  blocker: Blocker;
}

/**
 * Manages the session TTL countdown timer, navigation guard, and stats cache warming.
 *
 * - Finds the matching topic's TTL from community topics
 * - Counts down via useModerationCountdown
 * - Enables the navigation guard when there are unsaved cases and session is active
 * - Cache-warms topicModerationStats in Apollo cache for evict/refetch by other hooks
 */
export function useModerationTimerGuard({ communityId, topicName, sessionCases, isActive }: UseModerationTimerGuardParams): ModerationTimerGuardResult {
  // Session TTL countdown — find the matching topic's TTL and count down
  const { topics: communityTopics } = useCommunityTopics(communityId);
  const topicTtl = useMemo(() => {
    const matchingTopic = communityTopics.find(t => t.name.toLowerCase() === topicName.toLowerCase());

    return matchingTopic?.humanModerationAssignedTtlInSeconds ?? 0;
  }, [communityTopics, topicName]);
  const countdown = useModerationCountdown(topicTtl, isActive && sessionCases.length > 0);

  // Navigation guard — warn when leaving with unsubmitted reports
  // Disabled when session is inactive (stopped) or when the TTL has expired
  const { blocker } = useModerationNavigationGuard({ hasUnsavedWork: sessionCases.length > 0 && isActive && !countdown.isExpired });

  // Cache-warm: keeps topicModerationStats in Apollo cache for evict/refetch by other hooks
  // (useAssignModerationCase evicts on assign, useSubmitCaseFeedback refetches on submit)
  useQuery<GetTopicModerationStatsQuery>(GET_TOPIC_MODERATION_STATS, {
    variables: { communityId, topicName },
    skip: !communityId || !topicName,
    fetchPolicy: 'cache-and-network',
  });

  return { countdown, blocker };
}
